import {
  Modal,
  ModalOverlay,
  ModalContent,
  Text,
  Box,
  Flex,
  Button,
  FormControl,
  FormLabel,
  useMediaQuery,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuItemOption,
  MenuOptionGroup,
  Portal,
  Image,
  Input,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import { ChevronDownIcon } from '@chakra-ui/icons';
import Dropzone from 'react-dropzone';
import moment from 'moment';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { BsArrowRight } from 'react-icons/bs';

import UpdateBlockSchema from 'validation/schemas/UpdateBlockSchema';
import { handleFileDelete, handleFileUpload } from 'services/s3Service';
import FullScreenLoader from 'components/FullScreenLoader';
import { acceptedFormats } from 'utils/FileFormats';
import { CURRENCY_OPTIONS, MONTH_NAMES } from 'projectConstants';
import { UpdateModelProperties, FetchModelBlocks, GetUserModels } from 'redux/ModelsSlice';
import { getUserSubscriptionLimits } from 'redux/UserSlice';
import DatePickerIcon from 'components/Icons/DatePickerIcon';

import ModelSettingsDatePicker from './ModelSettingsDatePicker';

/** Match /model/{id}/time: send Jan–Dec abbrev, never 1–12. */
function fyStartMonthToApiAbbrev(value: string | undefined | null): string {
  if (value == null || String(value).trim() === '') {
    return 'Jan';
  }
  const s = String(value).trim();
  const match = MONTH_NAMES.find((m) => s === m || s.toLowerCase().startsWith(m.toLowerCase()));
  if (match) return match;
  const n = Number.parseInt(s, 10);
  if (!Number.isNaN(n) && n >= 1 && n <= 12) {
    return MONTH_NAMES[n - 1];
  }
  return 'Jan';
}

function getOwnerDisplay(modelData: Record<string, unknown> | undefined): {
  name: string;
  image?: string;
} {
  if (!modelData) return { name: 'N/A' };
  const md = modelData as {
    ownerName?: string;
    ownerImage?: string;
    creator?: string;
    owner?: {
      firstname?: string;
      lastname?: string;
      profile_image_url?: string;
      username?: string;
    };
  };
  if (md.ownerName) {
    return { name: md.ownerName, image: md.ownerImage };
  }
  const o = md.owner;
  if (o && typeof o === 'object') {
    const full = [o.firstname, o.lastname].filter(Boolean).join(' ').trim();
    return {
      name: full || o.username || md.creator || 'N/A',
      image: o.profile_image_url ?? md.ownerImage,
    };
  }
  return { name: md.creator || 'N/A', image: md.ownerImage };
}

interface ModelSettingsModalProperties {
  isOpen: boolean;
  onClose: () => void;
  modelData?: {
    name?: string;
    description?: string;
    bannerImage?: string;
    banner_image?: string;
    icon?: string;
    startDate?: Date;
    endDate?: Date;
    currency?: string;
    reporting_currency?: string;
    createdAt?: string;
    createdDate?: string;
    date_created?: string;
    ownerName?: string;
    ownerImage?: string;
    owner?: {
      firstname?: string;
      lastname?: string;
      profile_image_url?: string;
      username?: string;
    };
    creator?: string;
    time_granularity?: 'M' | 'Y';
    plan_duration_periods?: number;
    starting_period?: string;
    ending_period?: string;
    fy_start_month?: string;
    time_properties?: {
      time_granularity?: 'M' | 'Y';
      plan_duration_periods?: number;
      starting_period?: string;
      ending_period?: string;
      fy_start_month?: string;
    };
  };
  onSave?: (form: any) => void;
}

const ModelSettingsModal = ({
  isOpen,
  onClose,
  modelData,
  onSave,
}: ModelSettingsModalProperties) => {
  const { modelId } = useParams();
  const dispatch: any = useDispatch();
  const toast = useToast();
  const [isMobile] = useMediaQuery('(max-width: 575px)');
  const [isShortViewport] = useMediaQuery('(max-height: 820px)');
  const [bannerLoader, setBannerLoader] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [bannerImage, setBannerImage] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState(CURRENCY_OPTIONS[5]);
  const ownerDisplay = getOwnerDisplay(modelData as Record<string, unknown> | undefined);
  const timeProperties = modelData?.time_properties || modelData;

  useEffect(() => {
    const md = modelData as ModelSettingsModalProperties['modelData'] | undefined;
    setBannerImage(md?.bannerImage ?? md?.banner_image ?? null);
    const currencyCode = md?.currency ?? md?.reporting_currency;
    const nextCurrency =
      (currencyCode && CURRENCY_OPTIONS.find((c) => c.value === currencyCode)) ||
      CURRENCY_OPTIONS[5];
    setSelectedCurrency(nextCurrency);
  }, [modelData]);

  const [timescale, setTimescale] = useState<'M' | 'Y'>(
    (timeProperties?.time_granularity?.toUpperCase() as 'M' | 'Y') || 'M',
  );
  const [periodAmount, setPeriodAmount] = useState(
    timeProperties?.plan_duration_periods?.toString() ||
      (timeProperties?.time_granularity?.toLowerCase() === 'm' ? '36' : '3'),
  );
  const [maxValue, setMaxValue] = useState(36);
  const [fyStartMonth, setFyStartMonth] = useState(() =>
    fyStartMonthToApiAbbrev(timeProperties?.fy_start_month),
  );
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const UserSubscriptionLimitData = useSelector(getUserSubscriptionLimits);
  const timeGranualityLimit =
    UserSubscriptionLimitData?.subscription_features_limit?.model_timescale;

  // Helper function to parse date from period string
  const parseDateFromPeriod = (period: string, granularity: string): Date => {
    if (granularity === 'Y') {
      // Format: "YYYY"
      const year = Number.parseInt(period, 10);
      return new Date(year, 0, 1);
    }
    // Format: "DD MMM YY" or ISO string
    const parsed = new Date(period);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
    // Try parsing "DD MMM YY" format
    const parts = period.split(' ');
    if (parts.length === 3) {
      const day = Number.parseInt(parts[0], 10);
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = monthNames.indexOf(parts[1]);
      const year = 2000 + Number.parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    return new Date();
  };

  useEffect(() => {
    if (
      timeGranualityLimit === null &&
      UserSubscriptionLimitData?.current_subscription_name !== 'Ultimate'
    ) {
      setMaxValue(-1);
    } else {
      if (timescale.toLowerCase() === 'm') {
        setMaxValue(timeGranualityLimit);
      }
      if (timescale.toLowerCase() === 'y') {
        setMaxValue(timeGranualityLimit / 12);
      }
    }
  }, [timescale, UserSubscriptionLimitData, timeGranualityLimit]);

  // Parse dates from time_properties
  useEffect(() => {
    if (timeProperties) {
      const timeGranularity = timeProperties?.time_granularity?.toUpperCase() || 'M';
      setTimescale(timeGranularity as 'M' | 'Y');
      const value = Number.isNaN(timeProperties?.plan_duration_periods)
        ? timeGranularity === 'M'
          ? 36
          : 3
        : Number(timeProperties?.plan_duration_periods);
      setPeriodAmount(value.toString());
      setFyStartMonth(fyStartMonthToApiAbbrev(timeProperties?.fy_start_month));

      // Parse starting_period
      if (timeProperties?.starting_period) {
        const startDateParsed = parseDateFromPeriod(
          timeProperties.starting_period,
          timeGranularity,
        );
        setStartDate(startDateParsed);
      }

      // Parse ending_period or calculate from starting_period
      if (timeProperties?.ending_period) {
        const endDateParsed = parseDateFromPeriod(timeProperties.ending_period, timeGranularity);
        setEndDate(endDateParsed);
      } else if (timeProperties?.starting_period) {
        const startDateParsed = parseDateFromPeriod(
          timeProperties.starting_period,
          timeGranularity,
        );
        const endDateCalc = new Date(startDateParsed);
        const targetValue = timeGranularity === 'M' ? value : value * 12;
        const dateEnd = endDateCalc.setMonth(endDateCalc.getMonth() + targetValue);
        setEndDate(new Date(dateEnd));
      }
    }
  }, [timeProperties]);

  const formatDate = (date: string) => {
    return moment(date).format('DD MMM YYYY');
  };

  // const changeTimeInput = useCallback(
  //   (time: string, amount: string, startDateValue: Date | null, endDateValue?: Date | null) => {
  //     if (!modelId) return;

  //     const requestObject: any = {
  //       model_id: Number(modelId),
  //       request_body: {
  //         time_granularity: time,
  //         plan_duration_periods: Number(amount),
  //         fy_start_month: fyStartMonth,
  //       },
  //     };
  //     if (endDateValue) {
  //       const endInDate = endDateValue.toDateString();
  //       requestObject.request_body.ending_period =
  //         time === 'Y' ? dayjs(endInDate).format('YYYY') : dayjs(endInDate).format('DD MMM YY');
  //     }
  //     if (startDateValue !== null) {
  //       const startInDate = startDateValue.toDateString();
  //       requestObject.request_body.starting_period =
  //         time === 'Y' ? dayjs(startInDate).format('YYYY') : dayjs(startInDate).format('DD MMM YY');
  //     }
  //     dispatch(UpdateModelTime({ requestObject, toaster: true }));
  //   },
  //   [modelId, dispatch, fyStartMonth],
  // );

  const onChangePeriod = (valueDiff: any) => {
    const newValue = valueDiff;
    if (Number(newValue) <= maxValue) {
      setPeriodAmount(newValue.toString());
    }
    if (maxValue === -1) {
      setPeriodAmount(newValue.toString());
    }
  };

  const startDateChange = (startDateValue: Date) => {
    if (!endDate) return '0';
    if (timescale.toLowerCase() === 'm') {
      const monthsApart =
        (endDate.getFullYear() - startDateValue.getFullYear()) * 12 +
        (endDate.getMonth() - startDateValue.getMonth());
      setPeriodAmount(`${monthsApart}`);
      onChangePeriod(monthsApart);
      return `${monthsApart}`;
    }
    const yearDifference = endDate.getFullYear() - startDateValue.getFullYear();
    setPeriodAmount(yearDifference.toString());
    onChangePeriod(`${yearDifference}`);
    return `${yearDifference}`;
  };

  const endDateChange = (endDateValue: Date) => {
    if (!startDate) return '0';
    if (timescale.toLowerCase() === 'm') {
      const monthsApart =
        (endDateValue.getFullYear() - startDate.getFullYear()) * 12 +
        (endDateValue.getMonth() - startDate.getMonth());
      setPeriodAmount(`${monthsApart}`);
      onChangePeriod(monthsApart);
      return `${monthsApart}`;
    }
    const yearDifference = endDateValue.getFullYear() - startDate.getFullYear();
    setPeriodAmount(yearDifference.toString());
    onChangePeriod(`${yearDifference}`);
    return `${yearDifference}`;
  };

  const onSelectStartDate = (date: Date) => {
    setStartDate(date);
    const diff = startDateChange(date);
    onChangePeriod(Number(diff));
    // changeTimeInput(timescale, diff, date, endDate);
  };

  const onSelectEndDate = (date: Date) => {
    setEndDate(date);
    const diff = endDateChange(date);
    onChangePeriod(Number(diff));
    // changeTimeInput(timescale, diff, startDate, date);
  };

  const onChangeFiscalYear = (month: string) => {
    setFyStartMonth(month);
    // if (modelId && startDate && endDate) {
    //   const requestObject = {
    //     model_id: Number(modelId),
    //     request_body: {
    //       time_granularity: timescale,
    //       plan_duration_periods: Number(periodAmount),
    //       ending_period:
    //         timescale.toLowerCase() === 'y'
    //           ? dayjs(endDate).format('YYYY')
    //           : dayjs(endDate).format('DD MMM YY'),
    //       starting_period:
    //         timescale.toLowerCase() === 'y'
    //           ? dayjs(startDate).format('YYYY')
    //           : dayjs(startDate).format('DD MMM YY'),
    //       fy_start_month: `${month}`,
    //     },
    //   };
    //   dispatch(UpdateModelTime({ requestObject, toaster: true }));
    // }
  };

  const handleSubmit = async (form: any) => {
    if (!modelId) return;
    if (!startDate || !endDate) {
      toast({
        title: 'Start and end dates are required',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const tp = timeProperties as Record<string, string | undefined> | undefined;
    const iconValue = typeof form.icon === 'string' ? form.icon.trim() : '';
    const iconPayload = bannerImage || iconValue || (modelData as { icon?: string })?.icon || '';
    const requestBody = {
      model_payload: {
        name: form.name,
        description: form.description ?? '',
        reporting_currency: selectedCurrency.value,
        icon: iconPayload,
      },
      time_payload: {
        calendar_type: tp?.calendar_type ?? 'CALENDAR',
        time_granularity: timescale,
        plan_duration_periods: String(periodAmount),
        starting_period:
          timescale === 'Y' ? dayjs(startDate).format('YYYY') : dayjs(startDate).format('MMM-YY'),
        ending_period:
          timescale === 'Y' ? dayjs(endDate).format('YYYY') : dayjs(endDate).format('MMM-YY'),
        date_format: tp?.date_format ?? '%b-%y',
        fy_start_month: fyStartMonthToApiAbbrev(fyStartMonth),
      },
    };

    setIsSaving(true);
    try {
      await dispatch(
        UpdateModelProperties({
          modelId: Number(modelId),
          request_body: requestBody,
          toaster: true,
        }),
      ).unwrap();
      dispatch(FetchModelBlocks({ modelId: Number(modelId) }));
      dispatch(GetUserModels({}));

      const submitData = {
        ...form,
        bannerImage,
        startDate,
        endDate,
        currency: selectedCurrency.value,
        time_granularity: timescale,
        plan_duration_periods: Number(periodAmount),
        fy_start_month: fyStartMonth,
      };
      if (onSave) {
        onSave(submitData);
      }
      onClose();
    } catch {
      // Errors are surfaced via toast middleware
    } finally {
      setIsSaving(false);
    }
  };

  const handleBannerFileDrop = async (
    acceptedFiles: File[],
    setFieldValue?: (field: string, value: string) => void,
  ) => {
    if (acceptedFiles.length > 0) {
      setBannerLoader(true);
      const file = acceptedFiles[0];
      const data = await handleFileUpload(file, 'blox-user-assets');
      setBannerImage(data);
      setFieldValue?.('icon', data);
      setBannerLoader(false);
    }
  };

  const handleBannerDelete = async (setFieldValue?: (field: string, value: string) => void) => {
    if (bannerImage) {
      setBannerLoader(true);
      await handleFileDelete(bannerImage, 'blox-user-assets').then((response: any) => {
        if (!response?.error) {
          setBannerImage(null);
          setFieldValue?.('icon', '');
        }
      });
      setBannerLoader(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={isMobile ? 'full' : '2xl'} isCentered>
      <ModalOverlay bg='blackAlpha.600' />
      <ModalContent
        w='530px'
        maxW='530px'
        maxH={isShortViewport ? 'calc(100vh - 24px)' : 'unset'}
        borderRadius='16px'
        p={3}
        overflowY={isShortViewport ? 'auto' : 'visible'}
        sx={isShortViewport ? ({ scrollbarGutter: 'stable' } as any) : undefined}
      >
        <Formik
          key={[modelId, (modelData as { id?: number })?.id, modelData?.name].join('-')}
          enableReinitialize
          initialValues={{
            name: modelData?.name ?? '',
            description: modelData?.description ?? '',
            icon: modelData?.icon ?? '',
          }}
          onSubmit={(form) => handleSubmit(form)}
          validationSchema={UpdateBlockSchema}
        >
          {({ errors, values, touched, setFieldValue }: any) => {
            const iconUrl = typeof values.icon === 'string' ? values.icon.trim() : '';
            const bannerPreviewUrl = bannerImage || iconUrl || null;
            return (
              <Form>
                <Flex direction='column' gap={3}>
                  <Text
                    textAlign='center'
                    color='#121826'
                    fontSize='20px'
                    fontFamily='Comfortaa'
                    fontWeight='700'
                  >
                    Model Settings
                  </Text>

                  <Flex direction='column' gap={5}>
                    <Flex gap={5} direction={isMobile ? 'column' : 'row'}>
                      <Flex
                        flex={1}
                        p={2}
                        bg='#E8E9EB'
                        borderRadius='10px'
                        border='1px solid #E8E9EB'
                        justify='center'
                        align='center'
                        gap={2}
                      >
                        <Text color='#121826' fontSize='14px' fontWeight='500'>
                          Created :
                        </Text>
                        <Text color='#121826' fontSize='14px' fontWeight='400'>
                          {formatDate(
                            modelData?.createdAt ||
                              modelData?.createdDate ||
                              modelData?.date_created ||
                              new Date().toISOString(),
                          )}
                        </Text>
                      </Flex>
                      <Flex
                        flex={1}
                        p={2}
                        bg='#E8E9EB'
                        borderRadius='10px'
                        border='1px solid #E8E9EB'
                        justify='center'
                        align='center'
                        gap={2}
                      >
                        <Text color='#121826' fontSize='14px' fontWeight='500'>
                          Owner :
                        </Text>
                        <Flex align='center' gap={2}>
                          {ownerDisplay.image && (
                            <Image src={ownerDisplay.image} boxSize='20px' borderRadius='full' />
                          )}
                          <Text color='#121826' fontSize='14px' fontWeight='400'>
                            {ownerDisplay.name}
                          </Text>
                        </Flex>
                      </Flex>
                    </Flex>

                    <Box p={3} bg='#F5F0FF' borderRadius='10px' border='1px solid #F5F0FF'>
                      <Flex direction='column' gap={5}>
                        <FormControl>
                          <FormLabel m={0} mb={1} color='#708090' fontSize='12px' fontWeight='600'>
                            Fiscal year start month
                          </FormLabel>
                          <Menu closeOnSelect>
                            {/* @ts-ignore - Chakra union type complexity in tsserver */}
                            <MenuButton
                              as={Button}
                              rightIcon={<ChevronDownIcon color='#8B5CF6' />}
                              w='100%'
                              p={3}
                              h='40px'
                              justifyContent='space-between'
                              bg='white'
                              border='1px solid #E8E9EB'
                              borderRadius='10px'
                              color='#121826'
                              fontSize='14px'
                              fontWeight='500'
                              _hover={{ bg: 'white' }}
                              _active={{ bg: 'white' }}
                            >
                              {fyStartMonth}
                            </MenuButton>
                            <MenuList maxH='220px' overflowY='auto'>
                              <MenuOptionGroup value={fyStartMonth} type='radio'>
                                {MONTH_NAMES.map((month: string) => (
                                  <MenuItemOption
                                    key={month}
                                    value={month}
                                    onClick={() => onChangeFiscalYear(month)}
                                  >
                                    {month}
                                  </MenuItemOption>
                                ))}
                              </MenuOptionGroup>
                            </MenuList>
                          </Menu>
                        </FormControl>

                        <Flex gap={5} direction={isMobile ? 'column' : 'row'}>
                          <Box flex={1}>
                            <FormLabel
                              m={0}
                              mb={1}
                              color='#708090'
                              fontSize='12px'
                              fontWeight='600'
                            >
                              Timescale
                            </FormLabel>
                            <Flex align='center' w='100%'>
                              <ModelSettingsDatePicker
                                clearSection={false}
                                clearDateInput={() => setStartDate(null)}
                                selected={startDate}
                                onChange={onSelectStartDate}
                                timescale={timescale}
                                isDisabled={false}
                                placeholder='Start Date'
                                placeholderLabel='Start Date'
                                hideChevron={true}
                                emptyColor='#BEBEBE'
                                filledColor='#121826'
                                leftIcon={<DatePickerIcon />}
                                styles={{
                                  width: '100%',
                                  height: '40px',
                                  padding: '12px',
                                  background: 'white',
                                  borderRadius: '10px',
                                  border: '1px solid #E8E9EB',
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                  gap: '8px',
                                  display: 'inline-flex',
                                }}
                              />
                            </Flex>
                          </Box>
                          <Box flex={1}>
                            <FormLabel
                              m={0}
                              mb={1}
                              color='#708090'
                              fontSize='12px'
                              fontWeight='600'
                              opacity={0.01}
                            >
                              Model Name
                            </FormLabel>
                            <Flex align='center' w='100%'>
                              <ModelSettingsDatePicker
                                clearSection={false}
                                clearDateInput={() => setEndDate(null)}
                                selected={endDate}
                                onChange={onSelectEndDate}
                                timescale={timescale}
                                isDisabled={false}
                                placeholder='End Date'
                                placeholderLabel='End Date'
                                hideChevron={true}
                                emptyColor='#BEBEBE'
                                filledColor='#121826'
                                leftIcon={<DatePickerIcon />}
                                minDate={startDate || undefined}
                                styles={{
                                  width: '100%',
                                  height: '100%',
                                  padding: '12px',
                                  background: 'white',
                                  borderRadius: '10px',
                                  border: '1px solid #E8E9EB',
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                  gap: '8px',
                                  display: 'inline-flex',
                                }}
                              />
                            </Flex>
                          </Box>
                        </Flex>
                        <Flex align='center' gap={2} color='#708090' fontSize='12px'>
                          <BsArrowRight />
                          <Text>
                            {timescale === 'M'
                              ? `${Number(periodAmount) + 1} months`
                              : `${Number(periodAmount) + 1} years`}
                          </Text>
                        </Flex>
                      </Flex>
                    </Box>

                    <FormControl>
                      <Flex justify='space-between' align='center' mb={2}>
                        <Text color='#121826' fontSize='14px' fontWeight='600'>
                          Banner image
                        </Text>
                        <Text color='#708090' fontSize='14px' fontWeight='400'>
                          Recommended size: 1080x1920px.
                        </Text>
                      </Flex>
                      <Box position='relative'>
                        {bannerLoader && (
                          <FullScreenLoader
                            bgColor='linear-gradient(#FFFFFF, #F3F3F3)'
                            style={{ position: 'absolute', borderRadius: '10px' }}
                            height='114px'
                            width='100%'
                          />
                        )}
                        <Dropzone
                          onDrop={(acceptedFiles) =>
                            handleBannerFileDrop(acceptedFiles, setFieldValue)
                          }
                          accept={acceptedFormats}
                          maxSize={5 * 1024 * 1024}
                        >
                          {({ getRootProps, getInputProps }) => (
                            <Box
                              {...getRootProps()}
                              border='1px solid #708090'
                              borderRadius='10px'
                              py={2}
                              px={4}
                              height='114px'
                              overflow='hidden'
                              bg='white'
                              cursor='pointer'
                              display='flex'
                              alignItems='center'
                              justifyContent='center'
                              flexDirection='column'
                              gap={2}
                            >
                              <input {...getInputProps()} />
                              {bannerPreviewUrl ? (
                                <Flex direction='column' align='center' gap={2}>
                                  <Image
                                    src={bannerPreviewUrl}
                                    maxH='44px'
                                    borderRadius='8px'
                                    alt='Banner or model icon preview'
                                  />
                                  <Button
                                    size='sm'
                                    variant='outline'
                                    borderColor='#BEBEBE'
                                    color='#000000'
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (bannerImage) {
                                        handleBannerDelete(setFieldValue);
                                      } else if (iconUrl) {
                                        setFieldValue('icon', '');
                                      }
                                    }}
                                  >
                                    Remove
                                  </Button>
                                </Flex>
                              ) : (
                                <>
                                  <Text
                                    color='#8B5CF6'
                                    fontSize='12px'
                                    fontWeight='600'
                                    lineHeight='18px'
                                  >
                                    Click to upload
                                    <Text as='span' color='#121826' fontWeight='500' ml={1}>
                                      or drag and drop
                                    </Text>
                                  </Text>
                                  <Text
                                    color='#708090'
                                    fontSize='12px'
                                    fontWeight='400'
                                    lineHeight='18px'
                                  >
                                    JPG, JPEG, PNG less than 5MB
                                  </Text>
                                </>
                              )}
                            </Box>
                          )}
                        </Dropzone>
                      </Box>
                    </FormControl>

                    <Flex gap={5} direction={isMobile ? 'column' : 'row'}>
                      <FormControl flex={1}>
                        <FormLabel m={0} mb={1} color='#708090' fontSize='12px' fontWeight='600'>
                          Model Name
                        </FormLabel>
                        <Input
                          value={values.name}
                          onChange={(event) => setFieldValue('name', event.target.value)}
                          placeholder='Enter Model Name'
                          p={3}
                          h='40px'
                          border='1px solid #E8E9EB'
                          borderRadius='10px'
                          fontSize='14px'
                          fontWeight='500'
                          _placeholder={{ color: '#BEBEBE' }}
                          color='#000000'
                        />
                        {touched.name && errors.name && (
                          <Text color='red.500' fontSize='12px' mt={1}>
                            {errors.name}
                          </Text>
                        )}
                      </FormControl>
                      <FormControl flex={1}>
                        <FormLabel m={0} mb={1} color='#708090' fontSize='12px' fontWeight='600'>
                          Currency
                        </FormLabel>
                        <Menu strategy='fixed'>
                          {/* @ts-ignore - Chakra union type complexity in tsserver */}
                          <MenuButton
                            as={Button}
                            rightIcon={<ChevronDownIcon color='#8B5CF6' />}
                            w='100%'
                            p={3}
                            h='40px'
                            bg='white'
                            border='1px solid #E8E9EB'
                            borderRadius='10px'
                            textAlign='left'
                            color='#121826'
                            fontSize='14px'
                            fontWeight='500'
                            _hover={{ bg: 'white' }}
                            _active={{ bg: 'white' }}
                          >
                            {selectedCurrency ? selectedCurrency.label : 'Select Currency'}
                          </MenuButton>
                          <Portal>
                            <MenuList
                              key='currency-options-menu-list'
                              maxH='300px'
                              overflowY='auto'
                              overscrollBehavior='contain'
                              zIndex='popover'
                            >
                              {CURRENCY_OPTIONS.map((currency) => (
                                <MenuItem
                                  key={`${currency.value}-${currency.label}`}
                                  onClick={() => setSelectedCurrency(currency)}
                                >
                                  {currency.label}
                                </MenuItem>
                              ))}
                            </MenuList>
                          </Portal>
                        </Menu>
                      </FormControl>
                    </Flex>

                    <FormControl>
                      <FormLabel m={0} mb={1} color='#708090' fontSize='12px' fontWeight='600'>
                        Description
                      </FormLabel>
                      <Textarea
                        value={values.description}
                        onChange={(event) => setFieldValue('description', event.target.value)}
                        placeholder='Enter Description for 100 words max'
                        minH='72px'
                        p={3}
                        border='1px solid #E8E9EB'
                        borderRadius='10px'
                        fontSize='14px'
                        fontWeight='500'
                        resize='none'
                        _placeholder={{ color: '#BEBEBE' }}
                        color='#000000'
                      />
                      {touched.description && errors.description && (
                        <Text color='red.500' fontSize='12px' mt={1}>
                          {errors.description}
                        </Text>
                      )}
                    </FormControl>
                  </Flex>

                  <Flex gap={5} direction={isMobile ? 'column' : 'row'}>
                    <Button
                      flex={1}
                      p={4}
                      h='auto'
                      bg='white'
                      borderRadius='10px'
                      border='1px solid #BEBEBE'
                      color='#121826'
                      fontSize='14px'
                      fontWeight='500'
                      _hover={{ bg: 'gray.50' }}
                      onClick={onClose}
                      type='button'
                    >
                      Cancel
                    </Button>
                    <Button
                      flex={1}
                      p={4}
                      h='auto'
                      bg='#8B5CF6'
                      borderRadius='10px'
                      color='white'
                      fontSize='14px'
                      fontWeight='500'
                      _hover={{ bg: '#7C3AED' }}
                      type='submit'
                      isDisabled={bannerLoader || isSaving}
                      isLoading={isSaving}
                    >
                      Save Changes
                    </Button>
                  </Flex>
                </Flex>
              </Form>
            );
          }}
        </Formik>
      </ModalContent>
    </Modal>
  );
};

export default ModelSettingsModal;
